import React, { useState, useEffect } from 'react'
import axios from 'axios'
import {
  FileText,
  Search,
  Filter,
  Calendar,
  Download,
  Printer,
  ChevronDown,
  CheckCircle,
  AlertCircle,
  DollarSign,
  Receipt,
  TrendingUp,
  TrendingDown,
  Package,
  X,
  Loader,
  FileSpreadsheet,
  Eye
} from 'lucide-react'
import './ReportModule.css'

const API_URL = import.meta.env.VITE_API_URL || 'https://www.secs.gismac.co.ke/api'

const reportTypes = [
  { id: 'invoices', label: 'Invoice Report', icon: Receipt, endpoint: '/invoices', color: '#2196f3' },
  { id: 'budgets', label: 'Budget Report', icon: DollarSign, endpoint: '/finance/budgets', color: '#4caf50' },
  { id: 'expenses', label: 'Expense Report', icon: TrendingDown, endpoint: '/finance/expenses', color: '#f44336' },
  { id: 'receipts', label: 'Receipt Report', icon: Receipt, endpoint: '/receipts', color: '#ff9800' },
  { id: 'quotations', label: 'Quotation Report', icon: FileText, endpoint: '/quotations', color: '#9c27b0' },
  { id: 'suppliers', label: 'Supplier Report', icon: Package, endpoint: '/supply-chain', color: '#795548' }
]

const statusOptions = {
  invoices: ['Pending', 'Paid', 'Overdue', 'Cancelled', 'Draft'],
  budgets: ['Draft', 'Active', 'Closed', 'Cancelled'],
  expenses: ['Pending', 'Approved', 'Rejected', 'Reimbursed'],
  receipts: ['Valid', 'Cancelled', 'Pending'],
  quotations: ['Draft', 'Sent', 'Accepted', 'Rejected', 'Expired'],
  suppliers: ['Active', 'Inactive', 'Pending', 'Blacklisted']
}

const ReportModule = () => {
  const [selectedType, setSelectedType] = useState('')
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [amountMin, setAmountMin] = useState('')
  const [amountMax, setAmountMax] = useState('')
  const [message, setMessage] = useState({ type: '', text: '' })
  const [viewRecord, setViewRecord] = useState(null)

  const showMessage = (type, text) => {
    setMessage({ type, text })
    setTimeout(() => setMessage({ type: '', text: '' }), 4000)
  }

  const fetchRecords = async (typeId, filters = {}) => {
    if (!typeId) return
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const typeConfig = reportTypes.find(t => t.id === typeId)
      
      const params = new URLSearchParams()
      if (filters.status) params.append('status', filters.status)
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom)
      if (filters.dateTo) params.append('dateTo', filters.dateTo)
      if (filters.amountMin) params.append('amountMin', filters.amountMin)
      if (filters.amountMax) params.append('amountMax', filters.amountMax)
      
      const queryString = params.toString() ? `?${params.toString()}` : ''
      
      const res = await axios.get(`${API_URL}${typeConfig.endpoint}${queryString}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
     const data = res?.data?.data || res?.data || []
      setRecords(Array.isArray(data) ? data : [])
      showMessage('success', `${typeConfig.label} loaded: ${data.length} records`)
    } catch (err) {
      console.error('Fetch error:', err.response?.status, err.response?.data, err.message)
      setRecords([])
      showMessage('error', 'Failed to load records')
    } finally {
      setLoading(false)
    }
  }

  const handleTypeChange = (typeId) => {
    setSelectedType(typeId)
    setRecords([])
    setViewRecord(null)
    setSearchTerm('')
    setStatusFilter('')
    setDateFrom('')
    setDateTo('')
    setAmountMin('')
    setAmountMax('')
    setDropdownOpen(false)
    fetchRecords(typeId)
  }

  const applyFilters = () => {
    fetchRecords(selectedType, {
      status: statusFilter,
      dateFrom: dateFrom,
      dateTo: dateTo,
      amountMin: amountMin,
      amountMax: amountMax
    })
  }

  const filteredRecords = records.filter(r => {
    const search = searchTerm.toLowerCase()
    return JSON.stringify(r).toLowerCase().includes(search)
  })

  const handleExcelExport = () => {
    if (filteredRecords.length === 0) {
      showMessage('error', 'No records to export')
      return
    }
    
    const typeConfig = reportTypes.find(t => t.id === selectedType)
    const allKeys = new Set()
    filteredRecords.forEach(r => {
      Object.keys(r).forEach(k => {
        if (!k.startsWith('_') && k !== '__v') allKeys.add(k)
      })
    })
    const headers = Array.from(allKeys)
    
    const rows = filteredRecords.map(record => {
      return headers.map(h => {
        const val = record[h]
        if (val === null || val === undefined) return ''
        if (typeof val === 'object') return `"${JSON.stringify(val).replace(/"/g, '""')}"`
        return `"${String(val).replace(/"/g, '""')}"`
      })
    })
    
    const headerLabels = headers.map(h => 
      h.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
    )
    
    const csv = [headerLabels.join(','), ...rows.map(r => r.join(','))].join('\n')
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    
    let filename = `${typeConfig.id}_report`
    if (statusFilter) filename += `_status-${statusFilter}`
    if (dateFrom) filename += `_from-${dateFrom}`
    if (dateTo) filename += `_to-${dateTo}`
    if (amountMin) filename += `_min-${amountMin}`
    if (amountMax) filename += `_max-${amountMax}`
    filename += `_${new Date().toISOString().split('T')[0]}.csv`
    
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    
    showMessage('success', `${filteredRecords.length} records exported to Excel`)
  }

  const handlePrintReport = () => {
    if (filteredRecords.length === 0) {
      showMessage('error', 'No records to print')
      return
    }
    
    const typeConfig = reportTypes.find(t => t.id === selectedType)
    const formatDate = (dateStr) => {
      if (!dateStr) return 'N/A'
      try {
        return new Date(dateStr).toLocaleDateString('en-KE', {
          day: '2-digit', month: 'short', year: 'numeric'
        })
      } catch {
        return 'Invalid Date'
      }
    }
    
    const formatCurrency = (amount) => {
      return `KES ${(amount || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`
    }
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${typeConfig.label}</title>
        <style>
          @page { size: A4; margin: 15mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; 
            font-size: 11px; 
            line-height: 1.4; 
            color: #333;
            background: #f5f5f5;
            padding: 20px;
          }
          .print-page {
            width: 794px;
            min-height: 1123px;
            margin: 0 auto;
            background: white;
            padding: 30px;
            box-shadow: 0 0 20px rgba(0,0,0,0.1);
          }
          .print-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 20px;
            padding-bottom: 15px;
            border-bottom: 3px solid ${typeConfig.color};
          }
          .print-logo-section {
            display: flex;
            align-items: center;
            gap: 15px;
          }
          .print-logo {
            width: 60px;
            height: 60px;
            background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #1a237e 100%);
            border-radius: 50%;
            border: 3px solid #ffd700;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 2px;
          }
          .print-logo-g, .print-logo-s {
            font-size: 20px;
            font-weight: 900;
            color: #ffd700;
          }
          .print-logo-shield { font-size: 16px; }
          .print-company h1 {
            font-size: 18px;
            font-weight: 700;
            color: #1a237e;
            margin: 0;
          }
          .print-company p {
            font-size: 11px;
            color: #666;
            margin: 2px 0;
          }
          .print-doc-info {
            text-align: right;
          }
          .print-doc-type {
            display: inline-block;
            background: ${typeConfig.color};
            color: white;
            padding: 6px 16px;
            font-size: 14px;
            font-weight: 700;
            letter-spacing: 1px;
            text-transform: uppercase;
            border-radius: 4px;
            margin-bottom: 8px;
          }
          .print-doc-date {
            font-size: 12px;
            color: #666;
          }
          .print-summary {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 15px;
            margin-bottom: 20px;
          }
          .print-summary-box {
            background: #f8f9fa;
            padding: 12px;
            border-radius: 8px;
            text-align: center;
            border-left: 3px solid ${typeConfig.color};
          }
          .print-summary-number {
            font-size: 20px;
            font-weight: 700;
            color: #1a237e;
          }
          .print-summary-label {
            font-size: 11px;
            color: #888;
          }
          .print-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 10px;
          }
          .print-table th {
            background: ${typeConfig.color};
            color: white;
            padding: 8px 10px;
            text-align: left;
            font-weight: 600;
            font-size: 10px;
            text-transform: uppercase;
          }
          .print-table td {
            padding: 8px 10px;
            border-bottom: 1px solid #eee;
          }
          .print-table tr:nth-child(even) {
            background: #fafafa;
          }
          .print-footer {
            margin-top: 20px;
            padding-top: 15px;
            border-top: 2px solid #e0e0e0;
            text-align: center;
            font-size: 10px;
            color: #999;
          }
          @media print {
            body { background: white; padding: 0; }
            .print-page { box-shadow: none; width: 100%; padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="print-page">
          <div class="print-header">
            <div class="print-logo-section">
              <div class="print-logo">
                <span class="print-logo-g">G</span>
                <span class="print-logo-shield">🛡</span>
                <span class="print-logo-s">S</span>
              </div>
              <div class="print-company">
                <h1>Gismac Security Limited</h1>
                <p>Email: Finance@gismac.co.ke | www.secs.gismac.co.ke</p>
                <p>Nairobi, Kenya</p>
              </div>
            </div>
            <div class="print-doc-info">
              <div class="print-doc-type">${typeConfig.label}</div>
              <div class="print-doc-date">Generated: ${new Date().toLocaleDateString('en-KE')}</div>
            </div>
          </div>
          
          <div class="print-summary">
            <div class="print-summary-box">
              <div class="print-summary-number">${filteredRecords.length}</div>
              <div class="print-summary-label">Total Records</div>
            </div>
            <div class="print-summary-box">
              <div class="print-summary-number">${formatCurrency(filteredRecords.reduce((sum, r) => sum + (r.amount || r.totalAmount || r.total || 0), 0))}</div>
              <div class="print-summary-label">Total Amount</div>
            </div>
            <div class="print-summary-box">
              <div class="print-summary-number">${statusFilter || 'All'}</div>
              <div class="print-summary-label">Status Filter</div>
            </div>
            <div class="print-summary-box">
              <div class="print-summary-number">${dateFrom ? formatDate(dateFrom) : 'All'}</div>
              <div class="print-summary-label">Date From</div>
            </div>
          </div>
          
          <table class="print-table">
            <thead>
              <tr>
                ${Object.keys(filteredRecords[0]).filter(k => !k.startsWith('_') && k !== '__v').slice(0, 8).map(k => 
                  `<th>${k.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</th>`
                ).join('')}
              </tr>
            </thead>
            <tbody>
              ${filteredRecords.map(r => `
                <tr>
                  ${Object.keys(filteredRecords[0]).filter(k => !k.startsWith('_') && k !== '__v').slice(0, 8).map(k => {
                    let val = r[k]
                    if (val === null || val === undefined) return '<td>N/A</td>'
                    if (typeof val === 'object') return `<td>${JSON.stringify(val).substring(0, 30)}</td>`
                    if (k.toLowerCase().includes('date')) return `<td>${formatDate(val)}</td>`
                    if (k.toLowerCase().includes('amount') || k.toLowerCase().includes('total') || k.toLowerCase().includes('price')) return `<td>${formatCurrency(val)}</td>`
                    return `<td>${String(val).substring(0, 50)}</td>`
                  }).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="print-footer">
            <p>Generated by Gismac Security Management System | www.secs.gismac.co.ke</p>
            <p>This report is confidential and intended for authorized personnel only.</p>
          </div>
        </div>
      </body>
      </html>
    `
    
    const w = window.open('', '_blank')
    w.document.write(printContent)
    w.document.close()
    setTimeout(() => w.print(), 500)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A'
    try {
      return new Date(dateStr).toLocaleDateString('en-KE', {
        day: '2-digit', month: 'short', year: 'numeric'
      })
    } catch {
      return 'Invalid Date'
    }
  }

  const formatCurrency = (amount) => {
    return `KES ${(amount || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`
  }

  const selectedTypeConfig = reportTypes.find(t => t.id === selectedType)

  return (
    <div className="report-center">
      <div className="rc-header">
        <div>
          <h1><FileText size={28} /> Finance Report Center</h1>
          <p>Generate, query, print and export financial reports</p>
        </div>
      </div>

      {message.text && (
        <div className={`rc-alert rc-alert-${message.type}`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      {/* Modern Dropdown Report Type Selector */}
      <div className="rc-dropdown-container">
        <label className="rc-label"><Filter size={16} /> Select Report Type:</label>
        <div className="rc-dropdown">
          <button 
            className="rc-dropdown-toggle" 
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            {selectedTypeConfig ? (
              <>
                <selectedTypeConfig.icon size={18} style={{ color: selectedTypeConfig.color }} />
                <span>{selectedTypeConfig.label}</span>
              </>
            ) : (
              <><FileText size={18} /><span>Select Report Type...</span></>
            )}
            <ChevronDown size={16} className={`rc-chevron ${dropdownOpen ? 'open' : ''}`} />
          </button>
          
          {dropdownOpen && (
            <div className="rc-dropdown-menu">
              {reportTypes.map(type => {
                const Icon = type.icon
                return (
                  <button
                    key={type.id}
                    className={`rc-dropdown-item ${selectedType === type.id ? 'active' : ''}`}
                    onClick={() => handleTypeChange(type.id)}
                  >
                    <Icon size={18} style={{ color: type.color }} />
                    <span>{type.label}</span>
                    {selectedType === type.id && <CheckCircle size={14} style={{ color: type.color }} />}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Query Filters */}
      {selectedType && (
        <div className="rc-filters">
          <div className="rc-filter-row">
            <div className="rc-search-box">
              <Search size={16} />
              <input 
                type="text" 
                placeholder="Search records..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="rc-filter-group">
              <label><Filter size={12} /> Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All Statuses</option>
                {statusOptions[selectedType]?.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            
            <div className="rc-filter-group">
              <label><Calendar size={12} /> From</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            
            <div className="rc-filter-group">
              <label><Calendar size={12} /> To</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            
            <div className="rc-filter-group">
              <label><DollarSign size={12} /> Min Amount</label>
              <input type="number" value={amountMin} onChange={(e) => setAmountMin(e.target.value)} placeholder="0" />
            </div>
            
            <div className="rc-filter-group">
              <label><DollarSign size={12} /> Max Amount</label>
              <input type="number" value={amountMax} onChange={(e) => setAmountMax(e.target.value)} placeholder="∞" />
            </div>
            
            <button className="rc-btn-apply" onClick={applyFilters}>
              <Filter size={14} /> Apply
            </button>
          </div>
          
          {/* Action Buttons */}
          <div className="rc-actions">
            <button 
              className="rc-btn-excel" 
              onClick={handleExcelExport}
              disabled={filteredRecords.length === 0}
            >
              <FileSpreadsheet size={16} />
              Export Excel
            </button>
            <button 
              className="rc-btn-print" 
              onClick={handlePrintReport}
              disabled={filteredRecords.length === 0}
            >
              <Printer size={16} />
              Print Report
            </button>
            <span className="rc-filter-summary">
              Showing {filteredRecords.length} of {records.length} records
              {statusFilter && ` • Status: ${statusFilter}`}
              {dateFrom && ` • From: ${dateFrom}`}
              {dateTo && ` • To: ${dateTo}`}
            </span>
          </div>
        </div>
      )}

      {/* Results Table */}
      {loading ? (
        <div className="rc-loading">
          <Loader size={32} className="rc-spin" />
          <p>Loading records...</p>
        </div>
      ) : selectedType && filteredRecords.length === 0 ? (
        <div className="rc-empty">
          <FileText size={48} opacity={0.3} />
          <p>No records found</p>
          <span>Try adjusting your search or filters</span>
        </div>
      ) : selectedType ? (
        <div className="rc-results">
          <div className="rc-table-container">
            <table className="rc-table">
              <thead>
                <tr>
                  <th>#</th>
                  {Object.keys(filteredRecords[0]).filter(k => !k.startsWith('_') && k !== '__v').slice(0, 7).map(k => (
                    <th key={k}>{k.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</th>
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((record, idx) => (
                  <tr key={record._id || idx}>
                    <td>{idx + 1}</td>
                    {Object.keys(filteredRecords[0]).filter(k => !k.startsWith('_') && k !== '__v').slice(0, 7).map(k => {
                      let val = record[k]
                      let display = 'N/A'
                      if (val === null || val === undefined) display = 'N/A'
                      else if (typeof val === 'object') display = JSON.stringify(val).substring(0, 30)
                      else if (k.toLowerCase().includes('date')) display = formatDate(val)
                      else if (k.toLowerCase().includes('amount') || k.toLowerCase().includes('total') || k.toLowerCase().includes('price')) display = formatCurrency(val)
                      else display = String(val).substring(0, 40)
                      
                      return <td key={k}>{display}</td>
                    })}
                    <td>
                      <button className="rc-action-view" onClick={() => setViewRecord(record)} title="View">
                        <Eye size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="rc-empty">
          <FileText size={48} opacity={0.3} />
          <p>Select a report type from the dropdown to get started</p>
        </div>
      )}

      {/* View Modal */}
      {viewRecord && (
        <div className="rc-modal-overlay" onClick={(e) => e.target === e.currentTarget && setViewRecord(null)}>
          <div className="rc-modal">
            <div className="rc-modal-header">
              <h3><Eye size={20} /> Record Details</h3>
              <button className="rc-btn-close" onClick={() => setViewRecord(null)}><X size={20} /></button>
            </div>
            <div className="rc-modal-body">
              <div className="rc-detail-grid">
                {Object.entries(viewRecord).filter(([key]) => !key.startsWith('_') && key !== '__v').map(([key, val]) => (
                  <div key={key} className="rc-detail-item">
                    <label>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</label>
                    <span>{val === null || val === undefined ? 'N/A' : typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rc-modal-footer">
              <button className="rc-btn-secondary" onClick={() => setViewRecord(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ReportModule
